// ── useTimeCapsules ────────────────────────────────────────────────
// Query + publish helpers for Time Capsules (kind 34781).
//
// Capsules are addressable events. A user can only have one capsule per
// d-tag, and each capsule gets a fresh random d-tag, so dedup by newest
// per author+d is exactly the right behaviour (handled by useNostrQuery).

import { useMemo } from 'react';
import { useNostrQuery } from './useNostrQuery';
import { useNostrPublish } from './useNostrPublish';
import {
  CAPSULE_KIND,
  CAPSULE_TAG,
  parseCapsule,
  type TimeCapsule,
  type CapsuleVisibility,
} from '../lib/timeCapsule';

export interface PublishCapsuleInput {
  dTag: string;
  title: string;
  visibility: CapsuleVisibility;
  recipient?: string;
  unlockAt: number;
  round: number;
  ciphertext: string;
  txid?: string;
}

/** Publish (or re-publish, e.g. to attach a txid) a capsule event. */
export function useCapsulePublish() {
  const { publish } = useNostrPublish();

  const publishCapsule = async (input: PublishCapsuleInput): Promise<void> => {
    const now = Math.floor(Date.now() / 1000);
    const tags: string[][] = [
      ['d', input.dTag],
      ['t', CAPSULE_TAG],
      ['t', 'bch'],
      ['title', input.title],
      ['visibility', input.visibility],
      ['unlock', String(input.unlockAt)],
      ['round', String(input.round)],
    ];
    if (input.visibility === 'addressed' && input.recipient) {
      tags.push(['p', input.recipient]);
    }
    if (input.txid) tags.push(['txid', input.txid]);

    await publish({
      kind: CAPSULE_KIND,
      content: input.ciphertext,
      tags,
      created_at: now,
    });
  };

  return { publishCapsule };
}

/** Public feed of capsules — the discovery timeline. */
export function usePublicCapsules(limit = 100) {
  const { events, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [CAPSULE_KIND], '#t': [CAPSULE_TAG], limit }],
    enabled: true,
    allRelays: true,
  });

  const capsules = useMemo(() => {
    return events
      .map(parseCapsule)
      .filter((c): c is TimeCapsule => !!c && c.visibility === 'public')
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [events]);

  return { capsules, loading, refetch };
}

/** Capsules authored by a given pubkey (any visibility). */
export function useMyCapsules(pubkey: string | undefined, limit = 100) {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey ? [{ kinds: [CAPSULE_KIND], authors: [pubkey], limit }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const capsules = useMemo(() => {
    return events
      .map(parseCapsule)
      .filter((c): c is TimeCapsule => !!c)
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [events]);

  return { capsules, loading, refetch };
}

/** Capsules addressed TO a given pubkey (received). */
export function useReceivedCapsules(pubkey: string | undefined, limit = 100) {
  const { events, loading, refetch } = useNostrQuery({
    filters: pubkey ? [{ kinds: [CAPSULE_KIND], '#p': [pubkey], limit }] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  const capsules = useMemo(() => {
    return events
      .map(parseCapsule)
      .filter((c): c is TimeCapsule => !!c && c.visibility === 'addressed' && c.recipient === pubkey)
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [events, pubkey]);

  return { capsules, loading, refetch };
}
